import React, { useMemo, useState } from "react";
import { doc, updateDoc } from "firebase/firestore";
import { db } from "../../firebase";
import { useToast } from "../../components/ui/ToastProvider";
import { mapFirebaseError } from "../../utils/userErrors";

const CHORE_LIBRARY = [
  { title: "Make bed", points: 3, tags: ["morning", "room"] },
  { title: "Brush teeth", points: 2, tags: ["morning", "hygiene", "evening"] },
  { title: "Get dressed", points: 3, tags: ["morning"] },
  { title: "Pack backpack", points: 4, tags: ["school", "morning"] },
  { title: "Snack cleanup", points: 2, tags: ["after-school", "cleanup"] },
  { title: "Homework", points: 6, tags: ["after-school", "school"] },
  { title: "Tidy toys", points: 3, tags: ["evening", "cleanup"] },
  { title: "Put away laundry", points: 4, tags: ["weekend", "room"] },
  { title: "Set the table", points: 3, tags: ["evening", "responsibility"] },
  { title: "Feed pet", points: 3, tags: ["morning", "evening", "responsibility"] },
  { title: "Take out trash", points: 4, tags: ["weekend", "responsibility"] },
  { title: "Read for 15 minutes", points: 3, tags: ["after-school", "learning"] },
  { title: "Shower or bath", points: 3, tags: ["evening", "hygiene"] },
  { title: "Clean room floor", points: 5, tags: ["weekend", "cleanup", "room"] },
  { title: "Practice instrument", points: 5, tags: ["after-school", "learning"] }
];

const FOCUS_TAGS = [
  { id: "hygiene", label: "Hygiene" },
  { id: "cleanup", label: "Cleanup" },
  { id: "responsibility", label: "Responsibility" },
  { id: "learning", label: "Learning" },
  { id: "room", label: "Room Care" }
];

export default function AIRoutinesCard({ familyId, familySettings, canManage }) {
  const { showToast } = useToast();
  const [customName, setCustomName] = useState("");
  const [routineType, setRoutineType] = useState("morning");
  const [repeatType, setRepeatType] = useState("daily");
  const [customDays, setCustomDays] = useState([]);
  const [repeatDay, setRepeatDay] = useState(1);
  const [showAfterTime, setShowAfterTime] = useState("");
  const [count, setCount] = useState(4);
  const [focus, setFocus] = useState([]);
  const [preview, setPreview] = useState([]);
  const [errorText, setErrorText] = useState("");
  const [savingTemplate, setSavingTemplate] = useState(false);
  const templates = familySettings?.taskTemplates || [];

  const availableChores = useMemo(() => {
    const baseTag = routineType;
    const focusTags = new Set(focus);
    return CHORE_LIBRARY.filter(chore => {
      if (!chore.tags.includes(baseTag)) return false;
      if (focusTags.size === 0) return true;
      return chore.tags.some(tag => focusTags.has(tag));
    });
  }, [routineType, focus]);

  const buildChores = () => {
    const pool = availableChores.length > 0 ? availableChores : CHORE_LIBRARY;
    const shuffled = [...pool].sort(() => 0.5 - Math.random());
    const selected = shuffled.slice(0, Math.max(2, Math.min(8, Number(count) || 4)));
    return selected.map(item => ({
      title: item.title,
      points: item.points,
      frequency: "repeat",
      repeatType,
      customDays: repeatType === "custom" ? customDays : [],
      repeatDay: repeatType === "weekly" ? repeatDay : 1,
      expiryTime: "",
      showAfterTime: showAfterTime || ""
    }));
  };

  const generatePreview = () => {
    setErrorText("");
    setPreview(buildChores());
  };

  const generateTemplate = async () => {
    if (!canManage) return;
    if (Number(count) < 2 || Number(count) > 8) {
      setErrorText("Routine size must be between 2 and 8 chores.");
      return;
    }
    const chores = preview.length > 0 ? preview : buildChores();
    if (chores.length === 0) {
      setErrorText("No chores available for this routine.");
      return;
    }
    setErrorText("");
    setSavingTemplate(true);
    const id = `ai-${Date.now()}`;
    const name = (customName || `${routineType} routine`).trim();
    const nextTemplates = [...templates, { id, name, chores }];
    try {
      await updateDoc(doc(db, "families", familyId), { taskTemplates: nextTemplates });
      setCustomName("");
      setPreview([]);
      showToast({ type: "success", message: "AI routine template created." });
    } catch (error) {
      setErrorText(mapFirebaseError(error, "Unable to generate template."));
    } finally {
      setSavingTemplate(false);
    }
  };

  const toggleFocus = (id) => {
    setFocus(prev => (prev.includes(id) ? prev.filter(tag => tag !== id) : [...prev, id]));
  };

  return (
    <div className="glass parent-card">
      <div className="header-flex">
        <h2>AI Routine Builder</h2>
      </div>
      <div className="card-body">
        {!canManage && (
          <p style={{ textAlign: "center", opacity: 0.6 }}>You have view-only access.</p>
        )}
        {errorText && (
          <small className="pin-error" role="alert">
            {errorText}
          </small>
        )}
        {canManage && (
          <>
            <select value={routineType} onChange={(e) => setRoutineType(e.target.value)} disabled={savingTemplate}>
              <option value="morning">Morning Routine</option>
              <option value="after-school">After School</option>
              <option value="evening">Evening Wind Down</option>
              <option value="weekend">Weekend Reset</option>
            </select>
            <input
              placeholder="Template name (optional)"
              value={customName}
              onChange={(e) => setCustomName(e.target.value)}
              disabled={savingTemplate}
            />
            <div style={{ display: "flex", gap: "10px" }}>
              <input
                type="number"
                min="2"
                max="8"
                value={count}
                onChange={(e) => setCount(e.target.value)}
                style={{ width: "100px" }}
                disabled={savingTemplate}
              />
              <select value={repeatType} onChange={(e) => setRepeatType(e.target.value)} style={{ flex: 1 }} disabled={savingTemplate}>
                <option value="daily">Daily</option>
                <option value="weekdays">Weekdays</option>
                <option value="weekends">Weekends</option>
                <option value="weekly">Weekly</option>
                <option value="custom">Custom Days</option>
              </select>
            </div>

            {repeatType === "weekly" && (
              <select value={repeatDay} onChange={(e) => setRepeatDay(Number(e.target.value))} style={{ marginTop: "8px" }} disabled={savingTemplate}>
                {["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"].map((dayName, dayNum) => (
                  <option key={dayName} value={dayNum}>{dayName}</option>
                ))}
              </select>
            )}

            {repeatType === "custom" && (
              <div style={{ marginTop: "8px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px" }}>
                {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((dayName, dayNum) => (
                  <button
                    key={dayName}
                    className="btn-nav"
                    style={{ padding: "6px 10px", fontSize: "0.8rem" }}
                    onClick={() => {
                      setCustomDays(prev => (
                        prev.includes(dayNum)
                          ? prev.filter(d => d !== dayNum)
                          : [...prev, dayNum]
                      ));
                    }}
                    disabled={savingTemplate}
                  >
                    {customDays.includes(dayNum) ? `✓ ${dayName}` : dayName}
                  </button>
                ))}
              </div>
            )}

            <label style={{ marginTop: "8px", fontSize: "0.85rem", fontWeight: 600 }}>
              Show on board at (optional)
            </label>
            <input
              type="time"
              value={showAfterTime}
              onChange={(e) => setShowAfterTime(e.target.value)}
              disabled={savingTemplate}
            />

            <div style={{ marginTop: "8px" }}>
              <div style={{ fontSize: "0.85rem", fontWeight: 600, marginBottom: "6px" }}>
                Focus Areas (optional)
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                {FOCUS_TAGS.map(tag => (
                  <button
                    key={tag.id}
                    className="btn-nav"
                    style={{ padding: "6px 10px", fontSize: "0.8rem" }}
                    onClick={() => toggleFocus(tag.id)}
                    disabled={savingTemplate}
                  >
                    {focus.includes(tag.id) ? `✓ ${tag.label}` : tag.label}
                  </button>
                ))}
              </div>
            </div>

            <button className="btn-nav" onClick={generatePreview} disabled={savingTemplate}>
              Preview Routine
            </button>

            {preview.length > 0 && (
              <div style={{ marginTop: "8px", display: "grid", gap: "6px" }}>
                {preview.map((chore, index) => (
                  <div key={`${chore.title}-${index}`} className="parent-list-item" style={{ gap: "8px" }}>
                    <strong>{chore.title}</strong>
                    <span>⭐ {chore.points}</span>
                  </div>
                ))}
              </div>
            )}
            <button className="btn-nav success-btn" onClick={generateTemplate} disabled={savingTemplate}>
              {savingTemplate ? "Generating..." : "Generate Template"}
            </button>
            <small style={{ opacity: 0.7 }}>
              Uses a built-in chore library to assemble routines you can save as templates.
            </small>
          </>
        )}
      </div>
    </div>
  );
}
